/**
 * Host-declared client tools — wire types, schema-subset validator, and the
 * zod schema the chat route validates against (ADR-096).
 *
 * A host application may declare a bounded set of client-side tools on the
 * per-turn chat body. Server-side they are UNTRUSTED and only honored for
 * surfaces whose config opts in (`allowHostDeclaredTools`); the SDK reuses
 * `checkHostToolSchema` at authoring time so an unsupported construct fails
 * in the host's build rather than 400-ing every chat send.
 *
 * Lives in its own subpath (not `client-tools.ts`) because that module is
 * types-only by design — asserted by the contract's exports test.
 */

import { z } from "zod";
import { HOST_TOOL_NAME_RE, HOST_TOOL_NAMESPACE_SEPARATOR } from "./values";

/**
 * Model-visible name for a host-declared tool: the declaring surface's id,
 * the `__` separator, then the plain name the host author wrote
 * (`workforce__navigate_in_app`). Applied server-side; hosts never see it.
 */
export function hostToolWireName(surface: string, name: string): string {
  return `${surface}${HOST_TOOL_NAMESPACE_SEPARATOR}${name}`;
}

/**
 * Whether a model-visible tool name came from a host declaration. Sound
 * because plain names cannot contain `__` (HOST_TOOL_NAME_RE) and no
 * server-declared tool name does either (pinned by a registry test).
 */
export function isHostDeclaredWireName(name: string): boolean {
  return name.includes(HOST_TOOL_NAMESPACE_SEPARATOR);
}

export type HostToolJsonSchema =
  | {
      description?: string;
      enum?: string[];
      maxLength?: number;
      type: "string";
    }
  | {
      description?: string;
      maximum?: number;
      minimum?: number;
      type: "integer" | "number";
    }
  | { description?: string; type: "boolean" }
  | {
      description?: string;
      items: HostToolJsonSchema;
      maxItems?: number;
      type: "array";
    }
  | {
      additionalProperties: false;
      description?: string;
      properties: Record<string, HostToolJsonSchema>;
      required?: string[];
      type: "object";
    };

/** Root schema is always an object (mirrors `clientTool({ schema: z.object(…) })`). */
export type HostToolInputSchema = Extract<
  HostToolJsonSchema,
  { type: "object" }
>;

export interface HostDeclaredTool {
  /** Model-visible description. UNTRUSTED — authored by the host's browser code. */
  description: string;
  inputSchema: HostToolInputSchema;
  /**
   * The *plain* name the host author wrote (`navigate_in_app`). MUST match
   * `HOST_TOOL_NAME_RE`. The server namespaces it to
   * `{appSurface}__{name}` before the model ever sees it.
   */
  name: string;
}

export type HostToolSchemaCheck = { ok: true } | { ok: false; reason: string };

const ALLOWED_KEYS: Record<string, ReadonlySet<string>> = {
  array: new Set(["description", "items", "maxItems", "type"]),
  boolean: new Set(["description", "type"]),
  integer: new Set(["description", "maximum", "minimum", "type"]),
  number: new Set(["description", "maximum", "minimum", "type"]),
  object: new Set([
    "additionalProperties",
    "description",
    "properties",
    "required",
    "type",
  ]),
  string: new Set(["description", "enum", "maxLength", "type"]),
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function checkNode(value: unknown): HostToolSchemaCheck {
  if (!isPlainObject(value)) {
    return { ok: false, reason: "schema node must be a plain object" };
  }
  const type = value.type;
  if (typeof type !== "string" || !(type in ALLOWED_KEYS)) {
    return {
      ok: false,
      reason: `unsupported or missing "type" (${JSON.stringify(type)})`,
    };
  }
  const allowed = ALLOWED_KEYS[type];
  for (const key of Object.keys(value)) {
    if (!allowed?.has(key)) {
      return { ok: false, reason: `unsupported keyword "${key}"` };
    }
  }
  if (
    "description" in value &&
    value.description !== undefined &&
    typeof value.description !== "string"
  ) {
    return { ok: false, reason: '"description" must be a string' };
  }
  if (type === "array") {
    if (!("items" in value)) {
      return { ok: false, reason: 'array node requires "items"' };
    }
    return checkNode(value.items);
  }
  if (type === "object") {
    if (value.additionalProperties !== false) {
      return {
        ok: false,
        reason: 'object node requires "additionalProperties": false',
      };
    }
    if (!isPlainObject(value.properties)) {
      return { ok: false, reason: 'object node requires "properties"' };
    }
    const keys = Object.keys(value.properties);
    if ("required" in value && value.required !== undefined) {
      if (
        !Array.isArray(value.required) ||
        value.required.some((entry) => typeof entry !== "string")
      ) {
        return { ok: false, reason: '"required" must be a string array' };
      }
      for (const entry of value.required as string[]) {
        if (!keys.includes(entry)) {
          return {
            ok: false,
            reason: `"required" names undeclared property "${entry}"`,
          };
        }
      }
    }
    for (const child of Object.values(value.properties)) {
      const result = checkNode(child);
      if (!result.ok) {
        return result;
      }
    }
    return { ok: true };
  }
  if (
    type === "string" &&
    "enum" in value &&
    value.enum !== undefined &&
    (!Array.isArray(value.enum) ||
      value.enum.some((entry) => typeof entry !== "string"))
  ) {
    return { ok: false, reason: '"enum" must be a string array' };
  }
  return { ok: true };
}

/**
 * Validate that `value` is inside the host-declared schema subset (ADR-096
 * §D1): closed keyword set, object root. Size/depth are deliberately
 * unbounded — a host that bloats its schemas only wastes its own token
 * budget and prompt cache.
 *
 * Excluded on purpose, with reasons — do not "helpfully" add them:
 *  - `$ref` / `$defs`: unbounded expansion and cycles.
 *  - `oneOf` / `anyOf` / `allOf` / `not`: schema-size blow-up and poor model
 *    tool-choice behavior.
 *  - `pattern`: RE2-incompatible patterns are already stripped downstream
 *    (`sanitizeJsonSchemaForGateway`), so accepting them buys rejection risk
 *    with no upside.
 *  - `format`: the Ajv-strict hazard documented in
 *    `patches/@ai-sdk__workflow@1.0.9.patch`.
 *  - `default`: the workflow's `jsonSchema` defaults shim reads it, so a
 *    host-supplied default is silent argument injection.
 */
export function checkHostToolSchema(value: unknown): HostToolSchemaCheck {
  if (isPlainObject(value) && value.type !== "object") {
    return { ok: false, reason: 'root schema must have "type": "object"' };
  }
  return checkNode(value);
}

export const hostDeclaredToolSchema = z
  .object({
    // No length cap — a host that bloats its descriptions only wastes its
    // own token budget.
    description: z.string().min(1),
    // Structural validation happens in the `superRefine` below via
    // `checkHostToolSchema`; the custom type keeps `z.infer` assignable to
    // `HostDeclaredTool` without a second schema copy.
    inputSchema: z.custom<HostToolInputSchema>(
      (value) => typeof value === "object" && value !== null
    ),
    name: z.string().regex(HOST_TOOL_NAME_RE),
  })
  .superRefine((tool, ctx) => {
    const check = checkHostToolSchema(tool.inputSchema);
    if (!check.ok) {
      ctx.addIssue({
        code: "custom",
        message: `inputSchema: ${check.reason}`,
        path: ["inputSchema"],
      });
    }
  });

// No count cap — like description/schema size, a host that declares an
// excessive tool set only degrades its own copilot's tool choice and wastes
// its own token budget.
export const hostToolsSchema = z
  .array(hostDeclaredToolSchema)
  .superRefine((tools, ctx) => {
    const seen = new Set<string>();
    for (const tool of tools) {
      if (seen.has(tool.name)) {
        // Two tools with one name would make the model's tool map
        // order-dependent.
        ctx.addIssue({
          code: "custom",
          message: `duplicate tool name "${tool.name}"`,
        });
      }
      seen.add(tool.name);
    }
  });
